import { type LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData, Outlet, NavLink, useLocation } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { AlertError } from '#app/components/ui/alert-error.js'
import { Tabs, TabsList, TabsTrigger } from '#app/components/ui/tabs'
import { query as queryGithub } from '#app/graphql/clients/github.ts'
import { getOpenPRs } from '#app/graphql/queries/getOpenPRs.ts'
import { DEFAULT_REPOS, type Repository } from '#other/default-repos.js'

export async function loader({ request }: LoaderFunctionArgs) {
	const query = DEFAULT_REPOS.map(
		({ owner, name }) => `repo:${owner}/${name}`,
	).join(' ')
	const response = (await queryGithub(getOpenPRs, {
		query,
	})) as any

	if (response.errors) {
		console.error('Loader error:', response.errors)
	}

	const search = response.data.search
	if (search.edges.length !== search.repositoryCount) {
		console.error(
			'Not all repos are shown, please update getOpenPRs query to include all repos',
		)
	}

	search.edges.forEach(({ node: repository }: any) => {
		if (
			repository.pullRequests.edges.length !==
			repository.pullRequests.totalCount
		) {
			console.error(
				`Not all pull requests for repo: ${repository.name} are shown, please update getOpenPRs query to include all pull requests`,
			)
		}
	})

	return response
}

export default function AppRoute() {
	// TODO: how to type response?
	const response: any = useLoaderData<typeof loader>()
	const search = response.data.search

	const missingRepos = DEFAULT_REPOS.filter((repo: Repository) => {
		return !search.edges.find(({ node: repository }: any) => {
			return repository.nameWithOwner === `${repo.owner}/${repo.name}`
		})
	})

	let missingReposError = null
	if (missingRepos.length > 0) {
		missingReposError = `
					Missing pull requests from the following repos: ${missingRepos
						.map((repo: Repository) => `${repo.owner}/${repo.name}`)
						.join(', ')}. Please check that rhib0t has read access to these.
				`
	}

	// match tab value with pathname
	const location = useLocation()
	const pathname = location.pathname
	const regex = /\/app\/([^\/]+)$/
	const match = pathname.match(regex)

	let matchError = null
	let tabValue = 'summary'
	if (match) {
		tabValue = match[1]
	} else {
		matchError = 'No tab value found. Please click on a tab to render a view.'
	}

	return (
		<div className="container">
			<div className="align-end mb-2 flex flex-col justify-between sm:mb-0 sm:flex-row">
				<h1 className="mb-2 text-h2 sm:mb-6">Open Pull Requests</h1>
				<Tabs value={tabValue} activationMode="manual">
					<TabsList>
						<NavLink to="/app/summary" prefetch="intent">
							<TabsTrigger value="summary">Summary</TabsTrigger>
						</NavLink>
						<NavLink to="/app/list" prefetch="intent">
							<TabsTrigger value="list">List</TabsTrigger>
						</NavLink>
					</TabsList>
				</Tabs>
			</div>
			{missingReposError ? <AlertError message={missingReposError} /> : null}
			{matchError ? <AlertError message={matchError} /> : null}
			<Outlet />
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
